import { Link } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import type { PricingGridsResponse, ProductCard } from "@itaas/pep-contracts";
import { getPricingGrids, getProducts } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { usePepI18n } from "../i18n/usePepI18n";
import { productDisplayName, editionLabel } from "../utils/labels";
import { ProductPromotionBadge } from "../components/commercial/ProductPromotionBadge";
import { PromoPrice } from "../components/commercial/PromoPrice";
import {
  discountedAmount,
  isPromotionActive,
  resolveActivePromotion,
} from "../utils/product-promotion";

const STANDARD_EDITIONS = ["starter", "professional", "enterprise"] as const;

type ProductGrid = Record<string, number | null | string | undefined>;

function usesStandardEditions(editions: string[]): boolean {
  return editions.some((ed) => (STANDARD_EDITIONS as readonly string[]).includes(ed));
}

function pricingEditionKeys(product: ProductCard, grid: ProductGrid): string[] {
  const fromProduct = product.editions.filter(
    (ed) => ed !== "unit" && Object.prototype.hasOwnProperty.call(grid, ed),
  );
  if (fromProduct.length > 0) return fromProduct;
  return STANDARD_EDITIONS.filter((ed) => Object.prototype.hasOwnProperty.call(grid, ed));
}

interface PricingTableProps {
  products: ProductCard[];
  editions: readonly string[];
  productGrids: Record<string, ProductGrid>;
  locale: "fr" | "en";
  renderPriceCell: (product: ProductCard, edition: string, usd: number | null | undefined) => ReactNode;
  title?: string;
}

function PricingTable({
  products,
  editions,
  productGrids,
  locale,
  renderPriceCell,
  title,
}: PricingTableProps) {
  if (products.length === 0) return null;

  return (
    <div className="mb-4">
      {title ? <h2 className="h5 fw-bold mb-3">{title}</h2> : null}
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th>{locale === "en" ? "Product" : "Produit"}</th>
              {editions.map((ed) => (
                <th key={ed}>{editionLabel(ed)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const g = productGrids[p.slug];
              return (
                <tr key={p.slug}>
                  <td>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <strong>{productDisplayName(p, locale)}</strong>
                      <ProductPromotionBadge promotion={p.promotion} locale={locale} />
                    </div>
                    <div className="small text-secondary">{g.unit as string | undefined}</div>
                  </td>
                  {editions.map((ed) => (
                    <td key={ed}>{renderPriceCell(p, ed, g[ed] as number | null | undefined)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PricingPage() {
  const { locale } = usePepI18n();
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [currency, setCurrency] = useState<"USD" | "CDF" | "EUR">("USD");
  const [grids, setGrids] = useState<PricingGridsResponse | null>(null);

  useEffect(() => {
    getProducts().then(setProducts).catch(console.error);
    getPricingGrids().then(setGrids).catch(console.error);
  }, []);

  const productGrids = grids?.products ?? {};
  const rate =
    currency === "USD" ? (grids?.USD ?? 1) : currency === "CDF" ? (grids?.CDF ?? 2800) : (grids?.EUR ?? 0.92);
  const rdcDiscount =
    currency === "CDF" ? 1 - (grids?.rdcDiscountPercent ?? 30) / 100 : 1;

  function formatAmount(amount: number) {
    return new Intl.NumberFormat(currency === "CDF" ? "fr-CD" : "en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: currency === "CDF" ? 0 : 2,
    }).format(amount);
  }

  function formatPrice(usd: number | null | undefined) {
    if (usd === null || usd === undefined) return locale === "en" ? "Contact sales" : "Sur devis";
    return formatAmount(usd * rate * rdcDiscount);
  }

  function renderPriceCell(product: ProductCard, edition: string, usd: number | null | undefined) {
    if (usd === null || usd === undefined) {
      return locale === "en" ? "Contact sales" : "Sur devis";
    }
    const base = usd * rate * rdcDiscount;
    const promo = resolveActivePromotion(product.promotion, edition);
    if (promo) {
      const promoAmount = discountedAmount(base, promo.discountPercent);
      return (
        <PromoPrice original={formatAmount(base)} current={formatAmount(promoAmount)} />
      );
    }
    return formatPrice(usd);
  }

  const sourceLabel =
    grids?.source === "billing"
      ? locale === "en"
        ? "Synced from ITaaS Billing"
        : "Synchronisé depuis ITaaS Billing"
      : locale === "en"
        ? "Local indicative grid"
        : "Grille indicative locale";

  const productsWithPromo = products.filter((p) => isPromotionActive(p.promotion));

  const pricedProducts = products.filter((p) => productGrids[p.slug]);
  const standardProducts = pricedProducts.filter((p) => usesStandardEditions(p.editions));
  const verticalProducts = pricedProducts.filter((p) => !usesStandardEditions(p.editions));

  return (
    <SiteLayout>
      <SeoHead
        title={locale === "en" ? "ITaaS Pricing" : "Tarifs ITaaS"}
        description={
          locale === "en"
            ? "Indicative pricing USD, CDF and EUR. Per-product promotions from catalog."
            : "Grilles tarifaires indicatives USD, CDF et EUR. Promotions par produit depuis le catalogue."
        }
        keywords={["tarifs ITaaS", "prix ERP RDC", "abonnement logiciel Congo", "licence PME Afrique"]}
        canonicalPath="/pricing"
        locale={locale}
      />
      <div className="container py-5">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h1 className="display-6 fw-bold mb-2">{locale === "en" ? "Pricing" : "Tarifs"}</h1>
            <p className="text-secondary mb-2">
              {locale === "en"
                ? "Indicative grid — commercial validation required. Licensed software."
                : "Grille indicative — validation commerciale requise. Logiciels sous licence."}
            </p>
            {grids && (
              <span className={`badge ${grids.source === "billing" ? "text-bg-success" : "text-bg-secondary"}`}>
                {sourceLabel}
              </span>
            )}
          </div>
          <div className="btn-group">
            {(["USD", "CDF", "EUR"] as const).map((c) => (
              <button
                key={c}
                type="button"
                className={`btn btn-sm ${currency === c ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setCurrency(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {productsWithPromo.length > 0 && (
          <div className="alert alert-light border mb-4">
            <p className="small fw-semibold mb-2">
              {locale === "en" ? "Active promotions" : "Promotions en cours"}
            </p>
            <div className="d-flex flex-wrap gap-2">
              {productsWithPromo.map((p) => (
                <span key={p.slug} className="d-inline-flex align-items-center gap-2 small">
                  <strong>{productDisplayName(p, locale)}</strong>
                  <ProductPromotionBadge promotion={p.promotion} locale={locale} />
                </span>
              ))}
            </div>
          </div>
        )}

        {grids?.bundles && grids.bundles.length > 0 && (
          <div className="row g-3 mb-4">
            {grids.bundles.map((b) => (
              <div className="col-md-6" key={b.slug}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <h2 className="h6 fw-bold">{locale === "en" ? b.displayName : (b.displayNameFr ?? b.displayName)}</h2>
                    <p className="text-primary fw-semibold mb-1">{formatPrice(b.monthlyUsd)} / {b.unit}</p>
                    <p className="small text-secondary mb-0">{b.products.join(" · ")}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <PricingTable
          products={standardProducts}
          editions={STANDARD_EDITIONS}
          productGrids={productGrids}
          locale={locale}
          renderPriceCell={renderPriceCell}
        />

        {verticalProducts.map((p) => (
          <PricingTable
            key={p.slug}
            products={[p]}
            editions={pricingEditionKeys(p, productGrids[p.slug])}
            productGrids={productGrids}
            locale={locale}
            renderPriceCell={renderPriceCell}
            title={productDisplayName(p, locale)}
          />
        ))}

        <p className="small text-secondary mt-3">
          {locale === "en" ? "DRC -30% discount applied in CDF. " : "Remise RDC -30 % appliquée en CDF. "}
          {locale === "en"
            ? "Product promotions are configured per module in the catalog. "
            : "Les promotions sont configurées par produit dans le catalogue. "}
          <Link to="/pricing/editions">
            {locale === "en" ? "Compare editions →" : "Comparer les éditions →"}
          </Link>
        </p>
      </div>
    </SiteLayout>
  );
}
